// Core family (auth + current user). Buildings live in ./buildings.ts, reference data in ./reference.ts.
// Rule: implementations RESHAPE NomOS responses only; never recompute aggregates client-side.
//
// Token: NomOS sessions are per tenant (../sessions.ts); the active bearer is mirrored in localStorage `nomos_token`.
// changePassword/forgotPassword/resetPassword -> POST /auth/{change,forgot,reset}-password (forgot: email only — NomOS mails every membership).
// validateInvitation -> GET /auth/invites/{token};
// acceptInvitation -> POST /auth/accept-invite. login -> POST /auth/login {email,password} (+ /auth/login/choose when several organisations match).
import type { TokenResponse, User } from '@/types';
import { nomosFetch, resetTenantCaches, zulu, type NomosMe } from '../client';
import { saveSession } from '../sessions';
import { tenantZone } from '../tz';
import { chooseTenant as chooseNomosTenant, loginToTenant, resolveTenantFromMe, signIn, signOutActiveTenant, type LoginResponse } from '../tenant';
import type { AnyFn, NomosRegistry } from '../registry';
import { buildingsImpl } from './buildings';
import { referenceImpl, tenantFiscalStartMonth } from './reference';

/**
 * Email + password login (TZ-2). NomOS finds the organisation: one match → its session; several → throws
 * TenantChoiceRequired (../tenant.ts) and the login page shows the picker. `tenantSlug` (scripts) names one.
 * The session is held per tenant (../sessions.ts).
 */
async function login(email: string, password: string, tenantSlug?: string): Promise<TokenResponse> {
  const res = tenantSlug ? await loginToTenant(tenantSlug, email, password) : await signIn(email, password);
  return tokenResponse(res);
}

/** The login page's picker: the single-use choice token + one offered organisation → its session. */
async function chooseTenant(token: string, tenantSlug: string): Promise<TokenResponse> {
  return tokenResponse(await chooseNomosTenant(token, tenantSlug));
}

function tokenResponse(res: LoginResponse): TokenResponse {
  const expires_in = res.expires_at ? Math.max(0, Math.round((Date.parse(res.expires_at) - Date.now()) / 1000)) : 0;
  return { access_token: res.token, token_type: 'bearer', expires_in };
}

/** Revokes the active tenant's session on the server (best effort), then forgets it. */
async function logout(): Promise<void> {
  await signOutActiveTenant();
}

async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await nomosFetch('POST', '/auth/change-password', { current_password: currentPassword, new_password: newPassword }, { tenantScoped: false });
}
async function forgotPassword(email: string, tenantSlug?: string): Promise<void> {
  await nomosFetch('POST', '/auth/forgot-password', tenantSlug ? { tenant_slug: tenantSlug, email } : { email }, { tenantScoped: false });
}
async function resetPassword(token: string, newPassword: string): Promise<void> {
  await nomosFetch('POST', '/auth/reset-password', { token, new_password: newPassword }, { tenantScoped: false });
}

async function validateInvitation(token: string) {
  try {
    const inv = await nomosFetch<{ email: string; tenant_name?: string; is_admin: boolean; expires_at: string; status: string }>(
      'GET', `/auth/invites/${encodeURIComponent(token)}`, undefined, { tenantScoped: false });
    const valid = inv.status === 'open';
    return { valid, email: inv.email, role_name: inv.is_admin ? 'Administrator' : 'Member', expires_at: inv.expires_at,
      ...(valid ? {} : { error: `Invitation is ${inv.status}` }) };
  } catch (e) {
    if ((e as { response?: { status?: number } }).response?.status === 404) return { valid: false, error: 'Invitation not found' };
    throw e;
  }
}

async function acceptInvitation(token: string, data: { password: string; first_name: string; last_name: string; phone?: string }) {
  const res = await nomosFetch<LoginResponse>('POST', '/auth/accept-invite',
    { token, name: `${data.first_name} ${data.last_name}`.trim(), password: data.password }, { tenantScoped: false });
  if (res.member?.tenant_slug) {
    saveSession({ slug: res.member.tenant_slug, token: res.token, tenant_id: res.member.tenant_id,
      tenant_name: res.member.tenant_name ?? null, email: res.member.email, expires_at: res.expires_at });
    resetTenantCaches();
  }
  return { message: 'Invitation accepted', user_id: res.member?.member_id ?? '' };
}

/**
 * GET /auth/me -> NomOS GET /me (principal + the caller's own profile: role, hierarchy, active flag, last login)
 * + tenant fiscal start month. The member LIST is not read here: it is admin data, and /me already carries the
 * caller's own row. Gap: permissions (Sentinel permission strings have no NomOS equivalent).
 */
async function getCurrentUser(): Promise<User> {
  // The tenant calendar zone every query window needs travels with /me (first round trip), not after it.
  try { tenantZone().catch(() => undefined); } catch { /* no tenant yet: resolved after /me */ }
  // B7: /me decides the tenant of this session; every tenant-scoped call below uses it.
  const { me, session } = await resolveTenantFromMe();
  const fiscal = await tenantFiscalStartMonth();
  const m = me as NomosMe & { role_code?: string | null; hierarchy_level?: number | null; is_active?: boolean; last_login_at?: string | null; created_at?: string };
  return {
    id: me.sentinel_user_id ?? me.member_id, email: me.email,
    role: m.role_code ?? (me.is_admin ? 'admin' : 'member'),
    customer_id: me.tenant_id, customer_name: session.tenant_name || session.slug, fiscal_year_start_month: fiscal,
    is_active: m.is_active ?? true, permissions: [], hierarchy_level: m.hierarchy_level ?? (me.is_admin ? 100 : 0),
    last_login_at: zulu(m.last_login_at) ?? undefined, created_at: zulu(m.created_at),
  } as unknown as User;
}

export const coreImpl: NomosRegistry = {
  ...buildingsImpl,
  ...referenceImpl,
  login: login as unknown as AnyFn,
  chooseTenant: chooseTenant as unknown as AnyFn,
  logout: logout as unknown as AnyFn,
  getCurrentUser: getCurrentUser as unknown as AnyFn,
  validateInvitation: validateInvitation as unknown as AnyFn,
  acceptInvitation: acceptInvitation as unknown as AnyFn,
  changePassword: changePassword as unknown as AnyFn,
  forgotPassword: forgotPassword as unknown as AnyFn,
  resetPassword: resetPassword as unknown as AnyFn,
};
